import React, { ReactNode, useState, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useTrans, useTealium } from '@next-nx/hooks'
import { MenuItemType, OffertaType } from '@next-nx/utils/types'
import { Button, TabGroup, WContainer } from '@next-nx/shared-ui/atoms'
import { getTimestamp } from '@next-nx/utils'
import styles from './ProdottiHome.module.sass'
import ArrowRight from '../../atoms/Icons/ArrowRight'
import OffertaCard from '../../molecules/OffertaCard/OffertaCard'
import { CATEGORIA_AZIENDE_CODICE, CATEGORIA_PRIVATI_CODICE } from '@next-nx/utils/settings'


interface Props {
  item: MenuItemType | undefined
  offerte: OffertaType[]
  className?: string
}

interface TabContentProps {
  codiceCategoriaPrimaria: string
  offerte: OffertaType[]
}

const ProdottiHomeTabContent = (props: TabContentProps) => {
  const {
    codiceCategoriaPrimaria,
    offerte = []
  } = props

  return (
    <div className={styles.tabContent}>
      <Swiper slidesPerView={1.2} spaceBetween={30} breakpoints={{
        576: {
          slidesPerView: 1.8
        },
        768: {
          slidesPerView: 2.5
        },
        992: {
          slidesPerView: 4
        }
      }}>
        {offerte.map(x => (
          <SwiperSlide key={x.pk}>
            <OffertaCard offerta={x} codiceCategoriaPrimaria={codiceCategoriaPrimaria} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

const ProdottiHome = (props: Props) => {

  const { 
    item = undefined,
    offerte = [],
    className = '',
  } = props

  const t = useTrans()
  const { linkDataLayer, getListingProdottiData } = useTealium()

  const privatiItem = item?.children && item.children.edges.find(x => x.node.key === CATEGORIA_PRIVATI_CODICE)?.node || undefined
  const businessItem = item?.children && item.children.edges.find(x => x.node.key === CATEGORIA_AZIENDE_CODICE)?.node || undefined

  const tabs: {
    key: string
    label?: string
    disabled?: boolean
    content: ReactNode
  }[] = [
    ...privatiItem ? [{
      key: privatiItem.key || CATEGORIA_PRIVATI_CODICE,
      label: privatiItem.name,
      content: <ProdottiHomeTabContent offerte={offerte} codiceCategoriaPrimaria={CATEGORIA_PRIVATI_CODICE} />
    }] : [],
    ...businessItem ? [{
      key: businessItem.key || CATEGORIA_AZIENDE_CODICE,
      label: businessItem.name,
      content: <ProdottiHomeTabContent offerte={offerte} codiceCategoriaPrimaria={CATEGORIA_AZIENDE_CODICE}/>
    }] : []
  ]

  const [activeTab, setActiveTab] = useState(CATEGORIA_PRIVATI_CODICE)

  const ctaItem = activeTab === CATEGORIA_AZIENDE_CODICE ? businessItem : privatiItem

  const handleTracking = () => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: "action",
      action_name: "items_view", 
      action_tech: "view",
      action_type: "carosello",
      action_timestamp,
      action_effect: "visualizzazione_prodotti",
      view_items: [
        ...getListingProdottiData(offerte, CATEGORIA_PRIVATI_CODICE, 'homepage'),
        ...getListingProdottiData(offerte, CATEGORIA_AZIENDE_CODICE, 'homepage'),
      ],
    })
  }

  useEffect(() => {
    handleTracking()
  }, [])

  return item && offerte.length > 0 ? (
    <div className={`${styles.root} ${className}`}>
      <WContainer>
        <div className={styles.layout}>
          {!!item.name && <p className={styles.title}>{item.name}</p>}
        </div>
        <div className={styles.tabs}>
          <TabGroup tabs={tabs} triggersClassName={styles.tabsTriggers} setExternalActiveTab={setActiveTab} />
          {ctaItem?.ctaLabel && ctaItem?.url && (
            <div className={styles.ctaBox}>
              <Button label={ctaItem.ctaLabel} href={ctaItem.url} variant="ghost" icon={<ArrowRight />} iconPos="right" />
            </div>
          )}
        </div>
      </WContainer>
    </div>
  ) : <></>
}

export default ProdottiHome
